'use client';

import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import {
  ItemCard,
  ItemCardBody,
  ItemCardDivider,
  ItemCardHeader,
  ItemCardSection,
  ItemCardSummary,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import type {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCardExpiryStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getShortExpiryDate } from '@sola/shared-utils/credit-cards.ts';
import { slugify } from '@sola/utils/string.ts';

import { PaymentMethodIcon } from '../../atoms/payment-method-icon/payment-method-icon.tsx';
import { getPaymentMethodLabel } from '../../utils/payment-methods.ts';
import { CommitmentAvatarGroup } from '../commitment-avatar-group/commitment-avatar-group.tsx';
import { PaymentMethodCardFooter } from './payment-method-card-footer.tsx';

/**
 * Get payment method details.
 */
const getDetails = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) => {
  if (paymentMethod.creditCard) {
    const expirationDate: string = getShortExpiryDate(
      paymentMethod.creditCard.expirationDate,
    );
    if (!expirationDate) {
      return;
    }
    if (paymentMethod.creditCard.expiryStatus === 'EXPIRED') {
      return t('payments.methodDetails.expiredCard', {
        expirationDate,
        interpolation: { escapeValue: false },
      });
    }
    return t('payments.methodDetails.card', {
      expirationDate,
      interpolation: { escapeValue: false },
    });
  }
  if (paymentMethod.ach) {
    return paymentMethod.ach.accountName;
  }
};

export type PaymentMethodCardProps = {
  paymentMethodCommitments?: GraphQLCommitment[];
  paymentMethod: GraphQLSupporterPaymentMethod;
  allCommitments?: GraphQLCommitment[];
  onCommitmentsUpdated?: (updatedCommitments: GraphQLCommitment[]) => void;
};

/**
 * Get payment card data.
 */
export const usePaymentMethodCardData = ({
  paymentMethod,
  paymentMethodCommitments,
}: PaymentMethodCardProps) => {
  const { t } = useTranslation('my');
  return {
    t,
    title: getPaymentMethodLabel(paymentMethod, t),
    label: getDetails(paymentMethod, t),
    recurringCommitments: paymentMethodCommitments?.length
      ? paymentMethodCommitments
          .filter(
            (commitment) =>
              commitment.isRecurring &&
              commitment.status === CommitmentStatusEnum.Active,
          )
          .sort((a, b) => {
            const sponsorship = CommitmentTypeEnum.ChildSponsorship;
            if (
              a.commitmentType === sponsorship &&
              b.commitmentType !== sponsorship
            ) {
              return -1;
            }
            if (
              a.commitmentType !== sponsorship &&
              b.commitmentType === sponsorship
            ) {
              return 1;
            }
            return 0;
          })
      : [],
    // TODO will prob need to handle other cases.
    isWarning:
      paymentMethod.creditCard?.expiryStatus ===
      GraphQLCardExpiryStatus.ExpiringSoon,
    isError:
      paymentMethod.creditCard?.expiryStatus ===
      GraphQLCardExpiryStatus.Expired,
  };
};

const getDefaultHeading = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) =>
  paymentMethod.creditCard
    ? t('my:payments.methodTypes.creditCard.long')
    : t('my:payments.methodTypes.ach.long');

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = (props) => {
  const { title, label, isError, isWarning, recurringCommitments, t } =
    usePaymentMethodCardData(props);
  const { onCommitmentsUpdated } = props;
  const { paymentMethod, allCommitments } = props;
  const { accountNickname } = paymentMethod;

  const badgeProps: BadgeProps | null = useMemo(() => {
    const expStatus = paymentMethod.creditCard?.expiryStatus;
    if (!expStatus) {
      return null;
    }

    switch (expStatus) {
      case GraphQLCardExpiryStatus.ExpiringSoon: {
        return {
          color: 'warning',
          iconBefore: 'alert-triangle',
          label: t('cardExpiring'),
        };
      }
      case GraphQLCardExpiryStatus.Expired: {
        return {
          color: 'negative',
          iconBefore: 'alert-circle',
          label: t('cardExpired'),
        };
      }
    }
  }, [paymentMethod.creditCard?.expiryStatus]);

  return (
    <ItemCard
      className={cn(`PaymentMethodCard `, {
        'PaymentMethodCard--warning': isWarning,
        'PaymentMethodCard--error': isError,
        [`PaymentMethodCard--${slugify(paymentMethod.paymentType!)}`]:
          !!paymentMethod.paymentType,
      })}
      // Easy code > debatable rule...
      // eslint-disable-next-line no-nested-ternary
      color={isWarning ? 'warning' : isError ? 'negative' : undefined}
    >
      <ItemCardSection>
        <ItemCardHeader
          title={accountNickname || getDefaultHeading(paymentMethod, t)}
          badge={badgeProps ? <Badge responsive {...badgeProps} /> : undefined}
        />
        <ItemCardBody>
          <ItemCardSummary
            icon={<PaymentMethodIcon paymentMethod={paymentMethod} />}
            title={title}
            subTitle={label}
            isWarning={isWarning}
            isError={isError}
            isSubtleTitle
          />
          {recurringCommitments?.length ? (
            <CommitmentAvatarGroup
              commitments={recurringCommitments}
              tooltipMessage={t('payments.methodCommitmentsTooltip', {
                count: recurringCommitments.length,
              })}
            />
          ) : undefined}
        </ItemCardBody>
      </ItemCardSection>
      <ItemCardDivider />
      <PaymentMethodCardFooter
        recurringCommitments={recurringCommitments}
        allCommitments={allCommitments}
        paymentMethod={paymentMethod}
        onCommitmentsUpdated={onCommitmentsUpdated}
      />
    </ItemCard>
  );
};
